<template>
  <div class="dropdown"
       ref="dropdown"
       :title="title || 'Click to select a value'"
       :class="{ 'open': open }"
       @keydown="handleKeydown">
    <button class="toggle" ref="toggle" @click="toggleDropdown" :disabled="disabled">
      {{ selectedLabel }} &#x25BE;
    </button>
    <div class="options" :style="optionsStyle" v-if="open">
      <div class="filter-section" v-if="showFilter !== false" @click="focusFilter">
        <input class="filter" v-model="filterValue" ref="filter" />
      </div>
      <div class="filtered-options" :style="filteredOptionsStyle">
        <button v-for="option in filteredOptions"
             v-bind:value="option.value"
             class="option"
             :ref="'option-' + option.value"
             :class="{'selected': option.value === selectedValue}"
             @click="handleChange(option.value)">
          {{ option.text }}
      </button>
      </div>
      <div class="footer" :style="footerStyle">
        <button class="modal-button" @click="closeDropdown">
          Cancel
        </button>
      </div>
    </div>
  </div>
</template>

<script>
  import './DropDown.scss'

  const DEFAULT_MAX_HEIGHT = 200 // px

  export default {
    name: 'dropdown',

    props: ['value', 'options', 'disabled', 'showFilter', 'title'],

    data: function () {
      return {
        selectedValue: this.value,
        open: false,
        filterValue: '',
        optionsStyle: {},
        filteredOptionsStyle: {
          maxHeight: DEFAULT_MAX_HEIGHT + 'px'
        },
        footerStyle: {
          display: 'none'
        }
      }
    },

    computed: {
      selectedLabel: function () {
        const selectedOption = this.options.find(option => option.value === this.value)

        if (selectedOption) {
          return selectedOption.text
        } else {
          return '(select a value)'
        }
      },

      filteredOptions: function () {
        return this.filterOptions(this.options, this.filterValue)
      }
    },

    methods: {
      filterOptions: function (options, filterValue) {
        return options.filter(option => contains(option.text, filterValue))
      },

      toggleDropdown: function (event) {
        if (this.open) {
          this.closeDropdown()
        } else {
          this.openDropdown()
        }
      },

      openDropdown: function () {
        this.open = true
        this.selectedValue = this.value
        this.filterValue = ''

        this.updatePosition()

        // TODO: scroll to the selected value

        // set focus on the second next tick when the dropdown is rendered
        setTimeout(() => {
          setTimeout(() => {
            this.selectedValue = this.value
            this.focusSelectedValue()
            this.focusFilter()
          })
        })

        document.addEventListener('click', this.handleWindowEscape)
        document.addEventListener('wheel', this.handleWindowEscape)
        window.addEventListener('resize', this.updatePosition)
      },

      closeDropdown: function (focusToggle = true) {
        this.open = false
        this.filterValue = ''

        if (focusToggle) {
          this.focusToggle()
        }

        document.removeEventListener('click', this.handleWindowEscape)
        document.removeEventListener('wheel', this.handleWindowEscape)
        window.removeEventListener('resize', this.updatePosition)
      },

      focusFilter: function () {
        if (this.$refs.filter) {
          this.$refs.filter.focus()
        }
      },

      focusToggle: function () {
        // set focus on next tick so
        setTimeout(() => {
          if (this.$refs.toggle) {
            this.$refs.toggle.focus()
          }
        })
      },

      focusSelectedValue: function () {
        const ref = this.$refs['option-' + this.selectedValue]
        if (ref && ref[0]) {
          ref[0].focus()
        }
      },

      updatePosition () {
        if (this.$refs.dropdown) {
          const rect = this.$refs.dropdown.getBoundingClientRect()
          const body = document.querySelector('body').getBoundingClientRect()
          const maxHeight = Math.min(body.bottom - rect.top - 70, DEFAULT_MAX_HEIGHT)
          // the -70 is to compensate for the height of the filter input field

          this.optionsStyle = {
            left: rect.left + 'px',
            top: rect.bottom + 'px',
            minWidth: rect.width + 'px' // Needed for IE 11
          }

          this.filteredOptionsStyle = {
            maxHeight: maxHeight + 'px'
          }

          // must correspond with @media css for .options
          const showFooter = body.width < 600 || body.height < 600
          this.footerStyle = {
            display: showFooter ? '' : 'none'
          }
        }
      },

      // select previous value
      previous: function () {
        const options = this.filterOptions(this.options, this.filterValue)
        const index = options.findIndex(option => option.value === this.selectedValue)
        if (index !== -1 && index > 0) {
          this.selectedValue = options[index - 1].value
          this.focusSelectedValue()
          this.focusFilter()
        }
      },

      // select next value
      next: function () {
        const options = this.filterOptions(this.options, this.filterValue)
        const index = options.findIndex(option => option.value === this.selectedValue)
        if (index !== -1 && index < options.length - 1) {
          this.selectedValue = options[index + 1].value
          this.focusSelectedValue()
          this.focusFilter()
        }
      },

      handleChange: function (value) {
        this.closeDropdown()

        if (value) {
          this.$emit('change', value)
        }
      },

      handleKeydown: function (event) {
        if (this.open) {
          if (event.keyCode === 38 || (event.keyCode === 9 && event.shiftKey)) {
            // arrow up or shift+tab
            event.preventDefault()
            this.previous()
          }

          if (event.keyCode === 40 || (event.keyCode === 9 && !event.shiftKey)) {
            // arrow down or tab
            event.preventDefault()
            this.next()
          }

          if (event.keyCode === 13) { // enter
            event.preventDefault()
            this.handleChange(this.selectedValue)
          }

          if (event.keyCode === 27) { // ESC
            event.preventDefault()
            event.stopPropagation() // needed to prevent the dialog from being closed
            this.closeDropdown()
          }
        }
      },

      handleWindowEscape: function (event) {
        // close dropdown when clicking outside of the dropdown
        if (!isChildOf(event.target, this.$refs.dropdown)) {
          event.stopPropagation() // FIXME: stopPropagation doesn't work

          this.closeDropdown(false)
        }
      }
    },

    watch: {
      filterValue: function () {
        const options = this.filterOptions(this.options, this.filterValue)
        this.selectedValue = options.length ? options[0].value : undefined
        if (this.selectedValue) {
          this.focusSelectedValue()
          this.focusFilter()
        }
      }
    }
  }

  /**
   * case insensitive text search
   * @param {string} text     Text where to look for `search`
   * @param {string} search   Text to be found
   * @return {boolean} Returns true when found
   */
  function contains (text, search) {
    return text.toLowerCase().indexOf(search.toLowerCase()) !== -1
  }

  /**
   * Test whether an element is a child of a parent
   * @param {Element} elem
   * @param {Element} parent
   */
  function isChildOf (elem, parent) {
    let e = elem

    while (e) {
      if (e === parent) {
        return true
      }
      e = e.parentNode
    }

    return false
  }
</script>



// WEBPACK FOOTER //
// DropDown.vue?61e0e9d7